<template>
  <q-item
    v-if="title"
    clickable
    :class="['q-pa-sm', 'q-py-none', 'menu-list-item', ...(Array.isArray(customClasses) ? customClasses : [customClasses])]"
    :to="to">
    <q-item-section
      v-if="icon || loading"
      side>
      <q-spinner-gears
        v-if="loading"
        :class="['icon-instance', iconClass]"
        size="1.15rem"
        color="primary" />
      <q-icon
        v-else
        :name="icon!"
        :class="['icon-instance', iconClass]" />
    </q-item-section>

    <q-item-section>
      <q-item-label :class="titleClass">
        {{ title }}
      </q-item-label>

      <q-item-label
        v-if="hasSubtitle"
        class="text-caption q-mt-xs">
        <template v-if="typeof subtitle === 'string'">
          {{ subtitle }}
        </template>
        <q-badge
          v-else-if="subtitle && subtitle.text"
          class="status-pill"
          :color="subtitle.color || 'grey-7'"
          :text-color="subtitle.textColor || 'white'"
          :outline="subtitle.outline"
          :label="subtitle.text" />
      </q-item-label>
    </q-item-section>

    <q-item-section
      v-if="badge"
      side>
      <q-badge
        :color="badge.color || 'warning'"
        :label="badge.label"
        :text-color="badge.textColor || 'black'" />
    </q-item-section>
    <slot />
  </q-item>
  <q-skeleton
    v-else
    type="text" />
</template>

<script setup lang="ts">
import { computed } from 'vue'

interface Badge {
  label: string
  color?: string
  textColor?: string
}

interface Subtitle {
  text: string
  color?: string
  textColor?: string
  outline?: boolean
}

interface Props {
  title: string
  to?: string
  icon?: string
  iconClass?: string | string[] | Record<string, boolean>
  titleClass?: string
  customClasses?: (string | Record<string, boolean>)[]
  subtitle?: string | Subtitle
  badge?: Badge
  loading?: boolean
}

const props = defineProps<Props>()

const hasSubtitle = computed(() => {
  if (!props.subtitle) return false
  if (typeof props.subtitle === 'string') return props.subtitle.trim().length > 0
  return !!props.subtitle.text && props.subtitle.text.trim().length > 0
})
</script>

<style lang="scss" scoped>
.menu-list-item {
  border-radius: 8px;
  margin: 1px 8px;
  min-height: 36px;
  padding-top: 4px;
  padding-bottom: 4px;
  transition:
    background-color 0.15s cubic-bezier(0.4, 0, 0.2, 1),
    color 0.15s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    background-color: rgba(255, 255, 255, 0.05);
  }
}

.body--light .menu-list-item:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

.icon-instance {
  font-size: 1.15rem;
  padding: 0;
  border: none;
  border-radius: 0;
  opacity: 0.7;
  transition:
    color 0.15s cubic-bezier(0.4, 0, 0.2, 1),
    opacity 0.15s cubic-bezier(0.4, 0, 0.2, 1);
}

.menu-list-item:hover .icon-instance {
  opacity: 1;
}

// "has visible children" = expanded parent → icon goes primary, no box
.icon-instance.border-blue {
  border: none;
  color: var(--q-primary);
  opacity: 1;
}

.status-pill {
  border-radius: 999px;
  padding: 2px 9px;
  font-weight: 600;
  font-size: 0.64rem;
  letter-spacing: 0.03em;
  line-height: 1.35;
  text-transform: none;
}
</style>
